import { useCommonEntitiesStore } from "@/States/commonEntities";
import { trpc } from "@/Utils/trpc";
import { sortByOrderRank } from "@/Utils/wasteStreams";
import { useConfig } from "@/api/hooks/useConfig";
import { Button } from "@/components/button";
import { useAccessParents } from "@/pages/infrastructure/functions";
import { Translate, useTrans } from "@/translations";

import React, { useCallback, useEffect, useMemo } from "react";

import { uniqBy } from "lodash";
import { ACCESS_POINT } from "server/coreApi/accessPoints/types.js";

import {
  COLOR_CONFIG,
  COLOR_KEY,
  CUSTOM_COLORS,
  ColorsTable,
  DEFAULT_CONFIG,
} from "./colorsTable";
import { WasteTypeData, useConfigService } from "./useConfigService";
import { WasteStreamsTable } from "./wasteStreamsTable";

interface Props {
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
}

export const WasteStreams: React.FC<Props> = ({
  hasUnsavedChanges,
  setHasUnsavedChanges,
}) => {
  const { t, language } = useTrans();
  const { clientWasteTypeConfig: data, refetchClientWasteTypeConfig: refetch } =
    useConfigService();
  const { accessParents } = useAccessParents();
  const { config, updateConfig } = useConfig();
  const { wasteTypes: sanityWasteTypes } = useCommonEntitiesStore();

  const [updatedWasteTypes, setUpdatedWasteTypes] = React.useState<
    WasteTypeData[]
  >([]);
  const [updatedColors, setUpdatedColors] =
    React.useState<COLOR_CONFIG>(DEFAULT_CONFIG);

  useEffect(() => {
    const hasChangedColors = Object.keys(updatedColors).some(
      (c) =>
        (updatedColors[c as COLOR_KEY] || null) !==
        (config?.[c as COLOR_KEY] || null),
    );

    const hasChangedWasteTypes = !!updatedWasteTypes.some((wt) => {
      const originalWT = data?.find(
        (d) => d.wasteTypeCode === wt.wasteTypeCode,
      );

      if (!originalWT) {
        return wt.names.some((n) => n.displayName !== "") || wt.color !== "";
      } else {
        return (
          wt.names.some(
            (n) =>
              n.displayName !==
              originalWT.names.find((owt) => owt.locale === n.locale)
                ?.displayName,
          ) ||
          wt.color !== originalWT.color ||
          wt.downstreamHandlingId !== originalWT.downstreamHandlingId ||
          wt.co2factor !== originalWT.co2factor
        );
      }
    });

    setHasUnsavedChanges(hasChangedColors || hasChangedWasteTypes);
  }, [config, data, updatedColors, updatedWasteTypes, setHasUnsavedChanges]);

  useEffect(() => {
    const updatedColors: { [k in COLOR_KEY]: string } = { ...DEFAULT_CONFIG };

    CUSTOM_COLORS.forEach((color) => {
      updatedColors[color.key] = config[color.key] || "";
    });

    setUpdatedColors(updatedColors);
  }, [config, setUpdatedColors]);

  const { mutate: updateWasteTypeConfig } =
    trpc.config.updateWasteTypeConfig.useMutation({
      onSuccess: () => {
        refetch();
        setHasUnsavedChanges(false);
        setUpdatedWasteTypes([]);
      },
    });
  const containers = useMemo(() => {
    return sortByOrderRank(
      uniqBy(
        accessParents.flatMap((ap) => ap.containers as ACCESS_POINT[]),
        "wasteType.code",
      ),
      sanityWasteTypes || [],
      "wasteType.code",
    );
  }, [accessParents, sanityWasteTypes]);

  const onSave = useCallback(() => {
    if (updatedWasteTypes.length) {
      updateWasteTypeConfig(
        updatedWasteTypes.map((uwt) => ({
          ...uwt,
          co2factor: uwt.co2factor ? Number(uwt.co2factor) : null,
          costs:
            uwt.costs?.map((c) => ({
              ...c,
              validFrom: new Date(c.validFrom),
              validTo: c.validTo ? new Date(c.validTo) : null,
              costPerUnit: Number(c.costPerUnit),
            })) || [],
          locale: language,
        })),
      );
    }

    if (
      Object.values(updatedColors).filter(Boolean).length ||
      (!!config && Object.values(config).filter(Boolean).length)
    ) {
      updateConfig(updatedColors);
    }
  }, [
    updateWasteTypeConfig,
    language,
    updatedWasteTypes,
    updatedColors,
    updateConfig,
    config,
  ]);

  const onResetAll = useCallback(() => {
    setUpdatedWasteTypes(
      data?.map((el) => ({
        ...el,
        names: el.names.map((n) => ({ ...n, displayName: "" })),
        color: "",
      })) || [],
    );
    setUpdatedColors(DEFAULT_CONFIG);
  }, [setUpdatedWasteTypes, data]);

  return (
    <>
      <div className="mt-4">
        <ul className="ml-2 list-inside list-disc">
          <Translate i18nKey="hints:wasteStreamConfig">
            <li />
            <li>
              {
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                <a
                  href="/infrastructure/manage"
                  style={{ textDecoration: "underline" }}
                  rel="noreferrer"
                />
              }
            </li>
            <li />
          </Translate>
        </ul>
      </div>
      <ColorsTable
        updatedColors={updatedColors}
        setUpdatedColors={setUpdatedColors}
        setHasUnsavedChanges={setHasUnsavedChanges}
      />
      <WasteStreamsTable
        containers={containers}
        data={data}
        setHasUnsavedChanges={setHasUnsavedChanges}
        updatedWasteTypes={updatedWasteTypes}
        setUpdatedWasteTypes={setUpdatedWasteTypes}
      />

      <div className="sticky bottom-0 w-full bg-white pb-4">
        <hr></hr>
        <div className="mt-4 flex justify-between text-sm">
          <Button
            label={t("actions:save")}
            onClick={onSave}
            loading={false}
            disabled={!hasUnsavedChanges}
          />
          <div
            className="mr-4 cursor-pointer self-center underline"
            onClick={onResetAll}
          >
            {t("actions:resetAll")}
          </div>
        </div>
      </div>
    </>
  );
};
